import { useEffect, useRef } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { useAIExplorerChat } from "@/features/ai-explorer/hooks/use-ai-explorer-chat"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

import { useAgentSessionRun } from "./use-agent-session-run"

/**
 * Chat orchestration for a hosted-agent conversation (HERMES Fas 4).
 *
 * Deliberately a thin composition over the shared chat-turns stack rather
 * than a new chat implementation:
 *
 * - `useAIExplorerChat()` is called BARE (no options) so the durable
 *   chat-turns transport stays wired — the backend's hosted-agent run
 *   resolver reads the session's stamped `hostedAgentId` and dispatches the
 *   turn to Hermes; the frontend posts turns exactly as any other module.
 * - `useChatHistory` supplies URL `/agents/chat/:sessionId` → session sync
 *   and message hydration (thread switching).
 * - The hosted-agent BINDING (B6) is registered on `ChatService` while an
 *   agent view is mounted: any session the shared send path creates for this
 *   sessionType is then bound via `hosted_agent_id` on `/create-session`
 *   only. Existing sessions are never re-bound — the server stamps
 *   `visibility` at creation and refuses retroactive binding.
 */
export function useAgentChat(agent: HostedAgentSummary | undefined) {
  const chat = useAIExplorerChat()
  const { sessionType } = chat
  const explorerStore = useAIExplorerStore(sessionType)
  const queryClient = useQueryClient()

  const history = useChatHistory({
    sessionType,
    useChatStore: () => explorerStore,
  })

  // B6 binding lifecycle: set while this agent's view is mounted, cleared on
  // unmount/agent switch. The id-guarded unbind can't clobber the binding a
  // newer agent view has already registered.
  const agentId = agent?.id
  useEffect(() => {
    if (!agentId) return
    const chatService = ChatService.getInstance()
    chatService.bindHostedAgent(sessionType, agentId)
    return () => {
      chatService.unbindHostedAgent(sessionType, agentId)
    }
  }, [agentId, sessionType])

  // Keep the per-agent thread rail fresh: refetch when the active session
  // changes (a create-on-send just added a thread) and when a turn settles
  // (title generation / message counts landed server-side).
  const agentSlug = agent?.slug
  const { currentSessionId } = chat
  const isBusy = chat.chatState.isLoading || chat.chatState.isStreaming
  const wasBusyRef = useRef(false)
  useEffect(() => {
    if (!agentSlug) return
    void queryClient.invalidateQueries({ queryKey: queryKeys.hostedAgentSessions(agentSlug) })
  }, [agentSlug, currentSessionId, queryClient])
  useEffect(() => {
    if (wasBusyRef.current && !isBusy && agentSlug) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hostedAgentSessions(agentSlug) })
    }
    wasBusyRef.current = isBusy
  }, [agentSlug, isBusy, queryClient])

  // Run status (AgentRunningBanner pattern): a background run may still be
  // executing on the opened session — surface it while the chat is empty.
  // Re-derived from the registry after the local turn settles (#3207).
  const { chatState } = chat
  const { currentSessionAgentRun, isCurrentSessionAgentRunActive } = useAgentSessionRun(
    currentSessionId,
    isBusy || chatState.isAwaitingSave || Boolean(chatState.durableTurn)
  )

  return {
    ...chat,
    history,
    currentSessionAgentRun,
    isCurrentSessionAgentRunActive,
  }
}
